import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, TrendingUp, Users, Settings, UserPlus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { TrafficBadge } from "@/components/TrafficBadge";
import { KPISparkline } from "@/components/MemoAI/KPISparkline";
import { AppShell } from "@/components/layout/AppShell";
import { cn } from "@/lib/utils";
import { sanitizeError } from "@/lib/errorUtils";

interface Company {
  id: string;
  name: string;
  currency: string;
}

interface KPI {
  id: string;
  name: string;
  unit: string;
  min_band: number | null;
  max_band: number | null;
  owner_user_id: string;
  owner_profile?: { name: string };
}

interface KPIValue {
  value: number;
  week_start_date: string;
}

const CompanyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState<Company | null>(null);
  const [kpis, setKPIs] = useState<KPI[]>([]);
  const [kpiValues, setKPIValues] = useState<Record<string, KPIValue[]>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) loadCompanyData();
  }, [id]);

  const loadCompanyData = async () => {
    try {
      const [companyRes, kpisRes] = await Promise.all([
        supabase.from("companies").select("*").eq("id", id).single(),
        supabase.from("kpis").select("*").eq("company_id", id).order("sort_order"),
      ]);

      if (companyRes.error) throw companyRes.error;
      if (kpisRes.error) throw kpisRes.error;

      setCompany(companyRes.data);
      
      const kpisWithProfiles = await Promise.all(
        (kpisRes.data || []).map(async (kpi) => {
          const [profileRes, valuesRes] = await Promise.all([
            supabase.from("profiles").select("name").eq("id", kpi.owner_user_id).single(),
            supabase.from("kpi_values").select("value, week_start_date").eq("kpi_id", kpi.id).order("week_start_date", { ascending: true }).limit(12),
          ]);
          
          if (valuesRes.data) {
            setKPIValues(prev => ({ ...prev, [kpi.id]: valuesRes.data }));
          }
          
          return { ...kpi, owner_profile: profileRes.data || undefined };
        })
      );

      setKPIs(kpisWithProfiles);
    } catch (error: unknown) {
      toast.error(sanitizeError(error, "Failed to load company"));
      navigate("/app");
    } finally {
      setLoading(false);
    }
  };

  const getKPIStatus = (kpi: KPI): "green" | "amber" | "red" | "neutral" => {
    const values = kpiValues[kpi.id];
    if (!values || values.length === 0) return "neutral";
    const latestValue = values[values.length - 1]?.value;
    if (latestValue === undefined) return "neutral";
    if (kpi.min_band && latestValue < kpi.min_band) return "red";
    if (kpi.max_band && latestValue > kpi.max_band) return "red";
    if (values.length >= 2) {
      const previousValue = values[values.length - 2]?.value;
      if (previousValue) {
        const change = Math.abs((latestValue - previousValue) / previousValue);
        if (change > 0.2) return "amber";
      }
    }
    return "green";
  };

  if (loading) {
    return (
      <AppShell title="Loading..." showBack backTo="/app">
        <div className="flex items-center justify-center py-20">
          <div className="h-10 w-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
        </div>
      </AppShell>
    );
  }

  if (!company) return null;

  return (
    <AppShell
      title={company.name}
      subtitle={`${kpis.length} KPIs configured`}
      showBack
      backTo="/app"
      companyId={id}
      companyName={company.name}
    >
      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="max-w-6xl mx-auto space-y-8">
          <Tabs defaultValue="kpis" className="w-full">
            <TabsList className="w-full sm:w-auto grid grid-cols-4 sm:flex rounded-2xl bg-muted/50 p-1">
              <TabsTrigger value="kpis" className="rounded-xl text-xs sm:text-sm data-[state=active]:bg-background data-[state=active]:shadow-sm">
                <TrendingUp className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">KPIs</span>
              </TabsTrigger>
              <TabsTrigger value="team" className="rounded-xl text-xs sm:text-sm data-[state=active]:bg-background data-[state=active]:shadow-sm">
                <UserPlus className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Team</span>
              </TabsTrigger>
              <TabsTrigger value="recipients" className="rounded-xl text-xs sm:text-sm data-[state=active]:bg-background data-[state=active]:shadow-sm">
                <Users className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Recipients</span>
              </TabsTrigger>
              <TabsTrigger value="settings" className="rounded-xl text-xs sm:text-sm data-[state=active]:bg-background data-[state=active]:shadow-sm">
                <Settings className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Settings</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="kpis" className="space-y-6 mt-8">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <p className="text-muted-foreground">Track your key metrics and generate weekly reports</p>
                <Button 
                  onClick={() => navigate(`/app/companies/${id}/kpis/new`)} 
                  className="w-full sm:w-auto rounded-2xl bg-gradient-accent shadow-md"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add KPI
                </Button>
              </div>

              {kpis.length === 0 ? (
                <Card className="rounded-3xl border-dashed border-2 bg-card/50">
                  <CardContent className="py-16 text-center">
                    <div className="h-16 w-16 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-6">
                      <TrendingUp className="h-8 w-8 text-muted-foreground" />
                    </div>
                    <h3 className="text-xl font-semibold mb-2">No KPIs yet</h3>
                    <p className="text-muted-foreground mb-8 max-w-sm mx-auto">
                      Add 3-9 KPIs to start tracking your metrics and generating reports
                    </p>
                    <Button onClick={() => navigate(`/app/companies/${id}/kpis/new`)} className="rounded-2xl shadow-md bg-gradient-premium">
                      <Plus className="h-4 w-4 mr-2" />
                      Add first KPI
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4">
                  {kpis.map((kpi, index) => {
                    const status = getKPIStatus(kpi);
                    const values = kpiValues[kpi.id] || [];
                    const latestValue = values[values.length - 1]?.value;
                    
                    return (
                      <Card
                        key={kpi.id}
                        className={cn(
                          "cursor-pointer rounded-2xl border-border/50 transition-all duration-300",
                          "hover:shadow-lg hover:border-primary/20 hover:bg-card-hover",
                          "active:scale-[0.99]"
                        )}
                        onClick={() => navigate(`/app/companies/${id}/kpis/${kpi.id}`)}
                        style={{ animationDelay: `${index * 50}ms` }}
                      >
                        <CardHeader className="p-5">
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-4 min-w-0 flex-1">
                              <TrafficBadge status={status} />
                              <div className="min-w-0 flex-1">
                                <CardTitle className="text-base font-semibold truncate">{kpi.name}</CardTitle>
                                <CardDescription className="text-sm truncate">{kpi.owner_profile?.name || "Unassigned"}</CardDescription>
                              </div>
                            </div>
                            <div className="flex items-center gap-4 flex-shrink-0">
                              {values.length > 1 && (
                                <div className="hidden sm:block w-24 h-8">
                                  <KPISparkline 
                                    data={values.map(v => v.value)} 
                                    color={status === "red" ? "red" : status === "amber" ? "amber" : status === "green" ? "green" : "default"}
                                  />
                                </div>
                              )}
                              <div className="text-right">
                                <p className="text-lg font-semibold tabular-nums">
                                  {latestValue !== undefined ? `${latestValue}${kpi.unit}` : "—"}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {kpi.min_band && kpi.max_band ? `Target: ${kpi.min_band}–${kpi.max_band}${kpi.unit}` : "No target set"}
                                </p>
                              </div>
                            </div>
                          </div>
                        </CardHeader>
                      </Card>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            <TabsContent value="team" className="mt-8">
              <Card className="rounded-3xl">
                <CardContent className="py-12 text-center">
                  <div className="h-14 w-14 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-6">
                    <UserPlus className="h-7 w-7 text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Manage your team</h3>
                  <p className="text-muted-foreground mb-6 max-w-sm mx-auto">Invite team members to collaborate on KPIs</p>
                  <Button onClick={() => navigate(`/app/companies/${id}/team`)} className="rounded-2xl">
                    <UserPlus className="h-4 w-4 mr-2" />
                    Manage team
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="recipients" className="mt-8">
              <Card className="rounded-3xl">
                <CardContent className="py-12 text-center">
                  <div className="h-14 w-14 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-6">
                    <Users className="h-7 w-7 text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Configure recipients</h3>
                  <p className="text-muted-foreground mb-6 max-w-sm mx-auto">Set up who receives weekly KPI summaries</p>
                  <Button onClick={() => navigate(`/app/companies/${id}/recipients`)} className="rounded-2xl">
                    Manage recipients
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="settings" className="mt-8">
              <Card className="rounded-3xl">
                <CardContent className="py-12 text-center">
                  <div className="h-14 w-14 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-6">
                    <Settings className="h-7 w-7 text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Company settings</h3>
                  <p className="text-muted-foreground mb-6 max-w-sm mx-auto">Configure company details and reporting schedule</p>
                  <Button onClick={() => navigate(`/app/companies/${id}/settings`)} className="rounded-2xl">
                    Edit settings
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </AppShell>
  );
};

export default CompanyDetail;
